import React, {createContext, useEffect, useState} from "react";
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import jwtDecode from "jwt-decode";
import {Container} from "react-bootstrap";
import {getPathElementMap, getUnauthenticatedPathElementMap, unRedirectionPaths} from "AppRoutes";
import {TopMenu} from "Pages/TopMenu/TopMenu";
import {ReactNotifications} from "react-notifications-component";
import "react-notifications-component/dist/theme.css";
import {AppStyle} from "Styles/Pages.styles";
import {useCurrentUser} from "Models/User/useCurrentUser";
import theme, {AvailableThemes} from "theme";
import {ThemeProvider} from "styled-components";
import {LoadScript} from "@react-google-maps/api";
import GSPSFooter from "Components/Common/GSPSFooter";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {makePostRequest} from "Services/ServicesUtils";
import {API_USERS, WAFFLE_SCRIPT} from "Services/Endpoints";
import ChromeReminder from "Components/ChromeReminder/ChromeReminder";

// keep the initial state undefined as it will be checked in generateUserPermissionHook later.
export const UserContext = createContext();

const googleMapLibraries = ["places", "drawing", "geometry"];

const App = () => {
    useEffect(() => {
        loadWaffleScript();
    }, []);

    const loadWaffleScript = () => {
        const script = document.createElement("script");

        script.src = WAFFLE_SCRIPT;
        script.async = true;

        document.body.appendChild(script);
    };

    const isValidRefreshToken = () => {
        const token = localStorage.getItem("refresh_token");

        if (!token || token === "undefined") {
            return false;
        }
        const currentTime = new Date().getTime();
        const decoded = jwtDecode(token);
        return !((currentTime / 1000) > decoded["exp"]);
    };

    const [hasValidRefreshToken, setHasValidRefreshToken] = useState(Boolean(isValidRefreshToken()));
    const contextUser = useCurrentUser();
    const [currentTheme] = useState(AvailableThemes.light);

    // this call back is somehow important to update the current user data !!
    const [stateUser, setCurrentUser] = useState({});
    function SetCurrentLoggedUser(user) {
        useEffect(() => {
            setCurrentUser(user);
        }, [user]);
    }
    const currentLoggedUser = Object.keys(stateUser).length === 0 ? contextUser : stateUser;

    const authenticatedPathElementMap = getPathElementMap(SetCurrentLoggedUser, resetCurrentUserSession);
    const unauthenticatedPathElementMap = getUnauthenticatedPathElementMap(setHasValidRefreshToken);

    function resetCurrentUserSession(navigate) {
        makePostRequest(API_USERS.logout)
            .then(() => {
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                setHasValidRefreshToken(false);
                navigate("/");
            });
    }

    return (
        <>
            <ChromeReminder />
            <ReactNotifications />
            <ThemeProvider theme={theme[currentTheme]}>
                <AppStyle>
                    <Container fluid={true} className="p-0">
                        <BrowserRouter>
                            {hasValidRefreshToken &&
                                <Routes>
                                    {authenticatedPathElementMap.map((data) => {
                                        return (
                                            <Route
                                                key={data.path}
                                                path={data.path}
                                                element={
                                                    <>
                                                        <UserContext.Provider value={{user: currentLoggedUser}}>
                                                            <LoadScript
                                                                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                                                                libraries={googleMapLibraries}
                                                            >
                                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                    <TopMenu
                                                                        resetCurrentUserSession={
                                                                            resetCurrentUserSession
                                                                        }
                                                                    />
                                                                    {data.element}
                                                                    <GSPSFooter />
                                                                </LocalizationProvider>
                                                            </LoadScript>
                                                        </UserContext.Provider>
                                                    </>
                                                }
                                            />
                                        );
                                    })};
                                </Routes>
                            }
                            <Routes>
                                {unauthenticatedPathElementMap.map((data) => {
                                    if (hasValidRefreshToken &&
                                        location.pathname.startsWith("/public-audits/")) return null;
                                    return (
                                        <Route
                                            key={data.path}
                                            path={data.path}
                                            element={
                                                hasValidRefreshToken ?
                                                    <>
                                                        {!["*", "/"].includes(data.path) &&
                                                            !unRedirectionPaths.includes(data.path) &&
                                                            <Navigate to={{pathname: "/welcome"}} />
                                                        }
                                                    </> :
                                                    <>
                                                        <LoadScript
                                                            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                                                            libraries={googleMapLibraries}
                                                        >
                                                            {data.element}
                                                        </LoadScript>
                                                    </>
                                            }
                                        />
                                    );
                                })};
                            </Routes>
                        </BrowserRouter>
                    </Container>
                </AppStyle>
            </ThemeProvider>
        </>
    );
};

export default App;
